import * as SecureStore from 'expo-secure-store'
import { Platform } from 'react-native'
import { after, type LiveActivity } from 'expo-widgets'
import { TagoToursActivity, type TagoToursActivityProps } from './tago-tours-activity'
import type { TourTripSnapshot } from '../types/tours.types'

export interface TourLiveActivityRegistration {
  activityId: string
  pushToken: string | null
}

export interface TourLiveActivityPushTokenUpdate extends TourLiveActivityRegistration {
  bookingId: string
}

const ACTIVITY_KEY_PREFIX = 'tago.tours.live-activity.'
const STALE_AFTER_MS = 20 * 60 * 1000
const DISMISS_AFTER_MS = 15 * 60 * 1000
const startOperations = new Map<string, Promise<TourLiveActivityRegistration>>()

function storageKey(bookingId: string): string {
  return `${ACTIVITY_KEY_PREFIX}${bookingId}`
}

function findActivity(activityId: string): LiveActivity<TagoToursActivityProps> | null {
  return (
    TagoToursActivity.getInstances().find((activity) => activity.getId() === activityId) ?? null
  )
}

async function activityForBooking(bookingId: string): Promise<TourLiveActivityRegistration | null> {
  if (!supportsLiveActivities()) return null
  const activityId = await SecureStore.getItemAsync(storageKey(bookingId))
  if (!activityId) return null

  const activity = findActivity(activityId)
  if (!activity) {
    await SecureStore.deleteItemAsync(storageKey(bookingId))
    return null
  }

  return {
    activityId,
    pushToken: await activity.getPushToken().catch(() => null),
  }
}

async function startActivity(snapshot: TourTripSnapshot): Promise<TourLiveActivityRegistration> {
  const existing = await activityForBooking(snapshot.booking.id)
  if (existing) return existing

  const activity = TagoToursActivity.start(
    liveActivityPayload(snapshot),
    `tago://tours/trip/${encodeURIComponent(snapshot.booking.id)}`,
    new Date(Date.now() + STALE_AFTER_MS),
  )
  const activityId = activity.getId()
  if (!activityId) {
    await activity.end('immediate').catch(() => undefined)
    throw new Error('ActivityKit did not return an activity identifier')
  }

  try {
    await SecureStore.setItemAsync(storageKey(snapshot.booking.id), activityId)
  } catch (error) {
    // Without the persisted correlation a relaunch could create a duplicate.
    await activity.end('immediate').catch(() => undefined)
    throw error
  }

  return {
    activityId,
    pushToken: await activity.getPushToken().catch(() => null),
  }
}

/** ActivityKit is available from iOS 16.1; the generated extension targets iOS 16.4. */
function supportsLiveActivities(): boolean {
  if (Platform.OS !== 'ios') return false
  const version = typeof Platform.Version === 'string' ? Number.parseFloat(Platform.Version) : 0
  return Number.isFinite(version) && version >= 16.4
}

/**
 * ActivityKit boundary for Tours. The booking-to-activity mapping is persisted
 * because a Live Activity can outlive the React Native process that started it.
 */
export const toursLiveActivity = {
  async isAvailable(): Promise<boolean> {
    return supportsLiveActivities()
  },

  async activityForBooking(bookingId: string): Promise<TourLiveActivityRegistration | null> {
    return activityForBooking(bookingId)
  },

  /** ActivityKit push tokens can rotate; every new value replaces the server copy. */
  addPushTokenListener(
    bookingId: string,
    activityId: string,
    listener: (update: TourLiveActivityPushTokenUpdate) => void,
  ): () => void {
    if (!supportsLiveActivities()) return () => undefined
    const activity = findActivity(activityId)
    if (!activity) return () => undefined

    let active = true
    let lastPushToken: string | null = null
    const emit = (pushToken: string | null) => {
      if (!active || !pushToken || pushToken === lastPushToken) return
      lastPushToken = pushToken
      listener({ bookingId, activityId, pushToken })
    }
    const subscription = activity.addPushTokenListener((event) => {
      if (event.activityId !== activityId) return
      emit(event.pushToken)
    })

    // Subscribe first, then read the current token so a token emitted between
    // start() and the React effect is never lost.
    void activity
      .getPushToken()
      .then(emit)
      .catch(() => undefined)

    return () => {
      active = false
      subscription.remove()
    }
  },

  async start(snapshot: TourTripSnapshot): Promise<TourLiveActivityRegistration> {
    if (!supportsLiveActivities()) {
      throw new Error('Live Activities are unavailable on this device')
    }

    const bookingId = snapshot.booking.id
    const pending = startOperations.get(bookingId)
    if (pending) return pending

    const operation = startActivity(snapshot)
    startOperations.set(bookingId, operation)
    try {
      return await operation
    } finally {
      if (startOperations.get(bookingId) === operation) {
        startOperations.delete(bookingId)
      }
    }
  },

  async update(activityId: string, snapshot: TourTripSnapshot): Promise<void> {
    if (!supportsLiveActivities()) return
    const activity = findActivity(activityId)
    if (!activity) {
      await SecureStore.deleteItemAsync(storageKey(snapshot.booking.id))
      return
    }
    await activity.update(liveActivityPayload(snapshot), new Date(Date.now() + STALE_AFTER_MS))
  },

  async end(activityId: string, snapshot: TourTripSnapshot): Promise<void> {
    if (!supportsLiveActivities()) return
    const activity = findActivity(activityId)
    if (activity) {
      await activity.end(
        after(new Date(Date.now() + DISMISS_AFTER_MS)),
        liveActivityPayload(snapshot),
        new Date(),
      )
    }
    await SecureStore.deleteItemAsync(storageKey(snapshot.booking.id))
  },
}

export function liveActivityPayload(snapshot: TourTripSnapshot): TagoToursActivityProps {
  const currentPosition = snapshot.run?.currentStopPosition ?? 0
  const currentStop = snapshot.stops.find((stop) => stop.position === currentPosition)
  const nextStop = snapshot.stops.find((stop) => stop.position === currentPosition + 1)
  return {
    schemaVersion: 2,
    bookingId: snapshot.booking.id,
    title: snapshot.experience.title,
    originLabel: snapshot.experience.originLabel,
    destinationLabel: snapshot.experience.destinationLabel,
    startsAt: snapshot.departure.startsAt,
    endsAt: snapshot.departure.endsAt,
    status: snapshot.run?.status ?? 'scheduled',
    emergencyStatus: snapshot.run?.emergencyStatus ?? 'none',
    driverName: snapshot.driver?.displayName ?? null,
    driverVerified: snapshot.driver?.verified ?? false,
    passengerCount: snapshot.booking.passengerCount,
    currentStopLabel: currentStop?.label ?? snapshot.experience.originLabel,
    nextStopLabel: nextStop?.label ?? snapshot.experience.destinationLabel,
    currentStopPosition: currentPosition,
    stopCount: snapshot.stops.length,
    etaAt: snapshot.run?.etaAt ?? null,
    updatedAt: snapshot.run?.updatedAt ?? new Date().toISOString(),
  }
}
